import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useRegisterSW } from "virtual:pwa-register/react";
import {
  hasDeferredInstallPrompt,
  isInstallPromotionEnabled,
  subscribeInstallPrompt,
  triggerInstallPrompt,
} from "@/lib/installPrompt";

const INSTALL_DISMISSED_KEY = "maie.pwa.installDismissedAt";
// Re-offer install at most once per fortnight after a dismiss.
const REOFFER_AFTER_MS = 14 * 24 * 60 * 60 * 1000;

function shouldOfferInstall(): boolean {
  try {
    const raw = localStorage.getItem(INSTALL_DISMISSED_KEY);
    if (!raw) return true;
    const at = Number(raw);
    if (!Number.isFinite(at)) return true;
    return Date.now() - at > REOFFER_AFTER_MS;
  } catch {
    return true;
  }
}

function rememberInstallDismiss() {
  try {
    localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
  } catch {
    /* storage unavailable — fine, just nag again next session */
  }
}

export function PwaPrompts() {
  // Stash the live registration so visibility/route-change listeners
  // outside the SW-register callback can trigger an update check. The
  // `useRegisterSW` hook only exposes the registration via its onReg
  // callback, so we mirror it here.
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  // Tracks whether the user has touched the page yet. Used to decide
  // between "reload immediately for a fresh user" vs "silent skipWait
  // and defer to next navigation" when a new SW is detected. A
  // freshly-loaded tab (zero interactions) is the safest moment to
  // hard-swap to the new bundle — the symptom on iOS browsers is the
  // user always being "one update behind" because the bfcache-restored
  // page keeps running old in-memory JS; auto-reload at zero-touch
  // makes them current the moment they open the app.
  const interactedRef = useRef(false);
  const mountedAtRef = useRef(Date.now());
  const location = useLocation();

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;
      // Hourly heartbeat covers the long-tail "tab parked open for the
      // workday" case. The faster signals (visibility + route + pageshow)
      // below catch most deploys within seconds.
      const interval = window.setInterval(() => {
        registration.update().catch(() => {});
      }, 60 * 60 * 1000);
      return () => window.clearInterval(interval);
    },
  });

  // Mark "user has interacted" on the first input event. Once true we
  // treat the tab as mid-work and won't auto-reload — silent skipWait
  // path is the only one that fires.
  useEffect(() => {
    const mark = () => {
      interactedRef.current = true;
    };
    const opts = { once: true, passive: true } as AddEventListenerOptions;
    window.addEventListener("pointerdown", mark, opts);
    window.addEventListener("keydown", mark, opts);
    window.addEventListener("touchstart", mark, opts);
    window.addEventListener("wheel", mark, opts);
    return () => {
      window.removeEventListener("pointerdown", mark);
      window.removeEventListener("keydown", mark);
      window.removeEventListener("touchstart", mark);
      window.removeEventListener("wheel", mark);
    };
  }, []);

  // Re-check on tab focus. Cheap HEAD-like fetch against /sw.js —
  // does NOT activate a new SW on its own; it just kicks the browser
  // to download a new SW into the "waiting" slot if one exists.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      registrationRef.current?.update().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  // The big iOS Safari fix: bfcache restore. When the user backgrounds
  // the tab and returns, the entire page (including stale JS) is
  // resurrected from memory without any fresh navigation. The SW never
  // gets to check for updates. `pageshow` fires on every restore with
  // `persisted: true`; treat it like a fresh boot — force an update
  // check, and reset the "freshly opened" window so the auto-reload
  // path below can fire if a new SW is sitting waiting.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      mountedAtRef.current = Date.now();
      interactedRef.current = false;
      registrationRef.current?.update().catch(() => {});
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Re-check on every SPA route change. Cheap and surfaces a fresh
  // version within one click of a sidebar tap.
  useEffect(() => {
    registrationRef.current?.update().catch(() => {});
  }, [location.pathname]);

  // Activation policy when a new SW is detected:
  //
  //   - Within the first 4 seconds of mount AND user hasn't touched
  //     the page yet → reload to swap to the new bundle immediately.
  //     This is the "freshly opened tab" / "bfcache restore" case —
  //     the desktop pattern of "I open the app and see the new build"
  //     becomes true on mobile too. Nothing is in progress, so a
  //     ~200ms reload flash is non-disruptive and keeps mobile/iOS
  //     from being stuck "one update behind".
  //
  //   - Otherwise (user is mid-work) → DON'T yank the page out from
  //     under them. Surface a dismissible "Reload" toast and let them
  //     apply the update when they're ready. The new SW stays in the
  //     "waiting" state (workbox skipWaiting is now false) until they
  //     click — mid-recording / mid-chat / mid-anything is preserved.
  //
  // The prior behavior silently skip-waited (or, in the older
  // `autoUpdate` mode, unconditionally reloaded) — that's what read as
  // a "force update on every push".
  const updateToastRef = useRef(false);
  useEffect(() => {
    if (!needRefresh) return;
    const freshlyMounted =
      !interactedRef.current && Date.now() - mountedAtRef.current < 4_000;
    if (freshlyMounted) {
      updateServiceWorker(true).catch(() => {});
      return;
    }
    if (updateToastRef.current) return;
    updateToastRef.current = true;
    toast("A new version of MAIE is available.", {
      description: "Reload to update — your current work won't be lost.",
      duration: Infinity,
      action: {
        label: "Reload",
        onClick: () => updateServiceWorker(true).catch(() => {}),
      },
      onDismiss: () => {
        // Let it re-offer on the next update check (focus/route/heartbeat).
        updateToastRef.current = false;
      },
    });
  }, [needRefresh, updateServiceWorker]);

  // Defensive: if old in-memory JS tries to dynamically import a chunk
  // whose hash no longer exists in the new SW's precache, Vite emits
  // `vite:preloadError`. Reload at that point — it's a hard transition
  // either way, and a clean reload picks up the new bundle correctly.
  useEffect(() => {
    const onPreloadError = () => {
      window.location.reload();
    };
    window.addEventListener("vite:preloadError", onPreloadError);
    return () =>
      window.removeEventListener("vite:preloadError", onPreloadError);
  }, []);

  // Surface a passive install toast whenever the shared cache reports a
  // captured prompt (the actual `beforeinstallprompt` event is owned by
  // `@/lib/installPrompt` so the recording-gate modal can also trigger
  // it). Subscribe so a prompt that arrives mid-session still shows.
  useEffect(() => {
    let toastId: string | number | null = null;

    const offer = () => {
      // Install promotion is feature-flagged off until the native app
      // ships — see lib/installPrompt INSTALL_PROMO_ENABLED. The new-
      // version refresh toast above stays on; only the install offer
      // skips. Re-enable via VITE_INSTALL_PROMO=true.
      if (!isInstallPromotionEnabled()) return;
      if (!hasDeferredInstallPrompt()) return;
      if (!shouldOfferInstall()) return;
      if (toastId !== null) return;

      toastId = toast("Install MAIE for one-tap access.", {
        description: "Faster launch, full-screen, works offline.",
        duration: 12_000,
        action: {
          label: "Install",
          onClick: async () => {
            const outcome = await triggerInstallPrompt();
            if (outcome === "dismissed" || outcome === null) {
              rememberInstallDismiss();
            }
            if (toastId !== null) {
              toast.dismiss(toastId);
              toastId = null;
            }
          },
        },
        onDismiss: () => {
          rememberInstallDismiss();
          toastId = null;
        },
        onAutoClose: () => {
          toastId = null;
        },
      });
    };

    offer();
    const unsub = subscribeInstallPrompt(offer);

    const onInstalled = () => {
      toast.success("MAIE is installed.");
      if (toastId !== null) {
        toast.dismiss(toastId);
        toastId = null;
      }
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      unsub();
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return null;
}
